import React, { useMemo, useState } from 'react';
import { majDevis, supprimerDevis, listerDevis } from '../stockage/devis.js';
import { STATUTS, filtrer } from '../devis.js';
import { eur, cfa, reponseClient } from '../format.js';
import CarteResultat from '../composants/CarteResultat.jsx';
import CarteMarge from '../composants/CarteMarge.jsx';
import BoutonCopier from '../composants/BoutonCopier.jsx';
import Segmente from '../composants/Segmente.jsx';

/**
 * Écran Historique - document 04, écran 2.
 *
 * Le détail est en **lecture seule** sauf la note et le statut : un devis est
 * immuable. Rien n'est recalculé à l'affichage - tous les chiffres viennent du
 * `result` figé au moment de la cotation. Recalculer à partir des paramètres
 * courants afficherait un prix que le client n'a jamais reçu.
 */
export default function Historique({ devis, profils, onDevisChanges, onRejouer, onConfirmer, quotesInscrits }) {
  const [texte, setTexte] = useState('');
  const [statut, setStatut] = useState('');
  const [profilId, setProfilId] = useState('');
  const [ouvert, setOuvert] = useState(null);

  const visibles = useMemo(
    () => filtrer(devis, { texte, statut, profilId }),
    [devis, texte, statut, profilId]
  );

  const rafraichir = async (promesse) => { await promesse; onDevisChanges(await listerDevis()); };

  if (!devis.length) {
    return (
      <p style={{ color: 'var(--couleur-encre-douce)' }}>
        Aucun devis enregistré. Cotez un montant depuis l’écran Coter, puis enregistrez-le.
      </p>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--espace-4)' }}>
      <div style={{ display: 'grid', gap: 'var(--espace-3)' }}>
        <input
          type="search"
          value={texte}
          onChange={(e) => setTexte(e.target.value)}
          placeholder="Rechercher dans les notes et les profils"
          aria-label="Rechercher"
          className="champ"
          style={{ fontFamily: 'var(--police-texte)' }}
        />
        <div className="grille-2">
          <label>
            <span className="etiquette">Statut</span>
            <select value={statut} onChange={(e) => setStatut(e.target.value)} className="champ"
                    style={{ marginTop: 'var(--espace-1)', fontFamily: 'var(--police-texte)' }}>
              <option value="">Tous</option>
              {STATUTS.map((s) => <option key={s.cle} value={s.cle}>{s.libelle}</option>)}
            </select>
          </label>
          <label>
            <span className="etiquette">Profil</span>
            <select value={profilId} onChange={(e) => setProfilId(e.target.value)} className="champ"
                    style={{ marginTop: 'var(--espace-1)', fontFamily: 'var(--police-texte)' }}>
              <option value="">Tous</option>
              {profils.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
        </div>
      </div>

      <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--couleur-encre-douce)' }}>
        {visibles.length} devis sur {devis.length}
      </p>

      <div style={{ display: 'grid', gap: 'var(--espace-2)' }}>
        {visibles.map((d) => (
          <Ligne
            key={d.id}
            d={d}
            ouvert={ouvert === d.id}
            onBascule={() => setOuvert(ouvert === d.id ? null : d.id)}
            onStatut={(s) => rafraichir(majDevis(d, { status: s }))}
            onNote={(n) => rafraichir(majDevis(d, { note: n }))}
            onSupprimer={() => {
              if (window.confirm('Supprimer ce devis ?')) rafraichir(supprimerDevis(d.id));
            }}
            onRejouer={() => onRejouer(d)}
            // Un devis déjà inscrit au livre ne doit JAMAIS l'être une seconde
            // fois : la position et les caisses compteraient l'opération en
            // double, et rien ne le signalerait. On le sait en cherchant un
            // mouvement qui le référence, pas en se fiant à son statut - qu'un
            // clic dans la liste déroulante suffit à changer.
            dejaInscrit={quotesInscrits.has(d.id)}
            onConfirmer={(corridor) => onConfirmer(d, corridor)}
          />
        ))}
      </div>
    </div>
  );
}

function Ligne({ d, ouvert, onBascule, onStatut, onNote, onSupprimer, onRejouer, onConfirmer, dejaInscrit }) {
  const devise = d.profileSnapshot?.cfaCurrency ?? 'XAF';
  const r = d.result;
  const meta = STATUTS.find((s) => s.cle === d.status) ?? STATUTS[0];
  const sortant = r.direction === 'out';

  const resume = sortant
    ? `${eur(r.montantEnvoye)} → ${cfa(r.netCfa, devise)}`
    : `${cfa(r.montantCollecte, devise)} → ${eur(r.recuEur)}`;

  return (
    <article className="carte" style={{ padding: 'var(--espace-3)' }}>
      <button
        type="button"
        onClick={onBascule}
        aria-expanded={ouvert}
        style={{
          display: 'flex', width: '100%', gap: 'var(--espace-3)', alignItems: 'baseline',
          background: 'none', border: 'none', padding: 0, textAlign: 'left',
          color: 'var(--couleur-encre)', flexWrap: 'wrap',
        }}
      >
        <span style={{ flex: 1, minWidth: 180 }}>
          <strong>{d.note || 'Sans note'}</strong>
          <span className="chiffre" style={{ display: 'block', fontSize: '0.8rem', color: 'var(--couleur-encre-douce)' }}>
            {new Date(d.createdAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
            {' · '}{resume}
          </span>
        </span>
        <span style={{
          padding: '1px 8px', borderRadius: 'var(--rayon)', fontSize: '0.8rem',
          background: meta.couleur, color: 'var(--couleur-sur-accent)', whiteSpace: 'nowrap',
        }}>
          {meta.libelle}
        </span>
        <span className="chiffre" style={{
          fontSize: '0.85rem', fontWeight: 600,
          color: r.margeNette < 0 ? 'var(--couleur-alerte)' : 'var(--couleur-succes)',
        }}>
          {eur(r.margeNette)}
        </span>
      </button>

      {ouvert && (
        <div style={{ marginTop: 'var(--espace-4)', display: 'grid', gap: 'var(--espace-3)' }}>
          {/* Aucun recalcul : ces cartes lisent le résultat figé du devis. */}
          <CarteResultat r={r} devise={devise} basis={d.basis} />
          <CarteMarge r={r} officialRate={d.profileSnapshot.officialRate} />

          <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--couleur-encre-douce)' }}>
            Profil au moment de la cotation - {d.profileSnapshot.name} ·
            {' '}aller {d.profileSnapshot.rateOut} · retour {d.profileSnapshot.rateIn} ·
            {' '}{d.profileSnapshot.servicePercent} %
          </p>

          <label>
            <span className="etiquette">Note</span>
            <input
              type="text"
              defaultValue={d.note}
              onBlur={(e) => e.target.value !== d.note && onNote(e.target.value)}
              className="champ"
              style={{ marginTop: 'var(--espace-1)', fontFamily: 'var(--police-texte)' }}
            />
          </label>

          <label style={{ maxWidth: 240 }}>
            <span className="etiquette">Statut</span>
            <select
              value={d.status}
              onChange={(e) => onStatut(e.target.value)}
              className="champ"
              style={{ marginTop: 'var(--espace-1)', fontFamily: 'var(--police-texte)' }}
            >
              {STATUTS.map((s) => <option key={s.cle} value={s.cle}>{s.libelle}</option>)}
            </select>
          </label>

          <Confirmation
            devis={d}
            dejaInscrit={dejaInscrit}
            onConfirmer={onConfirmer}
          />

          <div style={{ display: 'flex', gap: 'var(--espace-3)', flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <BoutonCopier texte={reponseClient(r, devise)} />
            <button
              type="button"
              onClick={onRejouer}
              title="Recharge ces entrées dans Coter. Ce devis n’est pas modifié."
              style={{
                padding: 'var(--espace-2) var(--espace-4)', border: '1px solid var(--couleur-trait-fort)',
                borderRadius: 'var(--rayon)', background: 'transparent', color: 'var(--couleur-encre)',
              }}
            >
              Rejouer
            </button>
            <button
              type="button"
              onClick={onSupprimer}
              style={{
                padding: 'var(--espace-2) var(--espace-4)', border: '1px solid var(--couleur-trait-fort)',
                borderRadius: 'var(--rayon)', background: 'transparent', color: 'var(--couleur-alerte)',
              }}
            >
              Supprimer
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

/**
 * Confirmer une opération depuis l'Historique - document 04.
 *
 * « L'utilisateur confirme le devis, depuis l'écran Coter juste après, ou plus
 * tard depuis l'Historique. » Le second cas est en réalité le plus courant : on
 * cote aujourd'hui, le client donne suite demain.
 *
 * Confirmer n'est pas changer de statut. Le statut est une étiquette qu'un clic
 * suffit à corriger ; la confirmation engendre des mouvements immuables qui
 * déplacent des caisses et une position. C'est pourquoi elle est gardée, et
 * pourquoi elle ne se déduit pas du statut.
 */
function Confirmation({ devis, dejaInscrit, onConfirmer }) {
  const [ouvert, setOuvert] = useState(false);
  const [corridor, setCorridor] = useState(devis.profileSnapshot?.cfaCurrency === 'XOF' ? 'XOF' : 'XAF');
  const [enCours, setEnCours] = useState(false);
  const achat = devis.direction === 'out';

  if (dejaInscrit) {
    return (
      <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--couleur-encre-douce)' }}>
        Déjà inscrit au livre. Une erreur se corrige par un mouvement
        d’ajustement depuis l’écran Position, jamais en confirmant à nouveau.
      </p>
    );
  }

  if (!ouvert) {
    return (
      <div>
        <button
          type="button"
          onClick={() => setOuvert(true)}
          style={{
            padding: 'var(--espace-2) var(--espace-4)',
            border: '1px solid var(--couleur-accent)', borderRadius: 'var(--rayon)',
            background: 'transparent', color: 'var(--couleur-accent)', fontWeight: 600,
          }}
        >
          Confirmer l’opération
        </button>
      </div>
    );
  }

  return (
    <div className="carte" style={{ display: 'grid', gap: 'var(--espace-3)' }}>
      <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--couleur-encre-douce)' }}>
        Déclare que l’opération s’est faite et l’inscrit au livre :
        {achat
          ? <> un <strong>achat d’euros</strong>, la caisse CFA baisse et le stock monte.</>
          : <> une <strong>vente d’euros</strong>, la caisse CFA monte et le stock baisse.</>}
        {' '}Le devis lui-même reste figé.
      </p>
      <Segmente
        etiquette="Corridor de l’opération"
        valeur={corridor}
        onChange={setCorridor}
        options={[
          { valeur: 'XAF', libelle: 'XAF · CEMAC' },
          { valeur: 'XOF', libelle: 'XOF · UEMOA' },
        ]}
      />
      <div style={{ display: 'flex', gap: 'var(--espace-2)', flexWrap: 'wrap' }}>
        <button
          type="button"
          disabled={enCours}
          onClick={async () => {
            setEnCours(true);
            try { await onConfirmer(corridor); } finally { setEnCours(false); setOuvert(false); }
          }}
          style={{
            padding: 'var(--espace-2) var(--espace-4)',
            border: '1px solid var(--couleur-accent)', borderRadius: 'var(--rayon)',
            background: 'var(--couleur-accent)', color: 'var(--couleur-sur-accent)', fontWeight: 600,
          }}
        >
          Confirmer en {corridor}
        </button>
        <button
          type="button"
          onClick={() => setOuvert(false)}
          style={{
            padding: 'var(--espace-2) var(--espace-4)',
            border: '1px solid var(--couleur-trait-fort)', borderRadius: 'var(--rayon)',
            background: 'transparent', color: 'var(--couleur-encre)',
          }}
        >
          Annuler
        </button>
      </div>
    </div>
  );
}
